import ol = require("ol");
import MapView = require("../views/MapView");
import Portal = require("portal-gis");
import AgsLayers = require("../utils/ags/ags-layers");
import Utils = require("../utils");
import geo = require("../utils/geo");
import EventHandler = require("./base/event-handler");

const log = Utils.log;

class Handler {

    static about = `
Creates a layer suitable for rendering imagery provided by ArcGIS Server.

Usage Example: 

    <LAYER basemap="False" disabled="False" extent="-115.2589,36.15677,-115.1867,36.22891" id="parcels" maxlevel="24" minlevel="14" text="Parcels" type="app/layer-factory/arcgis-tile" url="https://usalvqa1.infor.com/arcgis/rest/services/IPS850/QASQL4850/MapServer">
    <OPTIONS>
        <Values id="layerType" value="rest" />
        <Values about="Parcels and Properties" id="showLayers" value="16" />
        <Values id="visible" value="true" />
        <Values id="layerDefs" value="{16:&quot;PRCLKEY&gt;1&quot;}" />
    </OPTIONS>
    </LAYER>

    `;

    static options = {
        "layerDefs": {
            "about": "rest only, allows you to filter the features of individual layers in the exported map by specifying definition expressions for those layers. Definition expression for a layer that is published with the service will be always honored.",
        },
        "layerType": {
            "about": `Identify the service type, use 'tile' to tile data with URLs in a set XYZ format that are defined in a URL template, use 'rest' to tile data from ArcGIS Rest services (map and image services are supported)
            `,
            "domain": "tile|rest",
            "default": "tile",
        },
        "refresh-interval": {
            "about": "time in seconds to wait before refreshing this layer",
        },
        "resolutions": {
            "about": "space separated values to specify the min and max resolutions for this layer",
        },
        "showLayers": {
            "about": "rest only, determines which layers appear on the exported map, comma-separated numeric values",
        },

        "visible": {
            "about": `controls the initial visibility of a layer, true to show the layer immediately, false otherwise`,
            "domain": "true|false",
            "default": "true",
        },
        "wkid": {
            "about": "if an 'extent' is provided it must be in 4326 but can be converted to the spatial reference specified here if the unlikely event the map is not using a 4326 projection"
        },
    }

    private static tile(l: Portal.MapLayerLeaf, view: MapView) {
        var sourceOptions = {
            url: `${l.url}/tile/{z}/{y}/{x}`
        };

        var options = Utils.asDictionary(l.Options.Values);

        var tileOptions = <olx.layer.TileOptions>{
            id: l.id,
            title: l.text,
            type: "base",
            visible: "false" !== options["visible"],
            source: new ol.source.XYZ(sourceOptions),
            componentType: l.componentType,
            assetType: l.assetType
        };

        // seems wrong...if providing an extent in 4326 why bother providing 4326 as wkid?
        if (l.extent) {
            tileOptions.extent = geo.transformExtent({ wkid: view.options.wkid }, geo.asExtent(l.extent)).geometry;
            log(`${l.id} bound by extent ${JSON.stringify(tileOptions.extent)}`);
        }

        Utils.ifOption(options, "scales", scales => {
            // TODO: how to convert scale to resolution?
        });

        Utils.ifOption(options, "resolutions", resolutions => {
            var res = resolutions.split(" ").map(parseFloat);
            tileOptions.minResolution = res[0];
            tileOptions.maxResolution = res[1];
            log(`${l.id} bound by resolutions: ${resolutions}`);
        });

        var tile = new ol.layer.Tile(tileOptions);

        tile.on("tileloadstart", () => {
            view.trigger("tileloadstart", {
                layer: l,
                tile: tile
            });
        });

        tile.on("tileloadend", () => {
            view.trigger("tileloadend", {
                layer: l,
                tile: tile
            });
        });


        AgsLayers.setMinMaxZoomLevel(view, tile, l);

        return tile;

    }

    private static rest(l: Portal.MapLayerLeaf, view: MapView) {

        var options = Utils.asDictionary(l.Options.Values);

        var sourceOptions = {
            url: l.url,
            params: {
                layers: "show:*",
                layerDefs: <string>null
            }
        };
    
        Utils.ifOption(options, "showLayers", showLayers => sourceOptions.params.layers = "show:" + showLayers);
        Utils.ifOption(options, "layerDefs", layerDefs => sourceOptions.params.layerDefs = layerDefs);

        var source = new ol.source.TileArcGISRest(sourceOptions);
        
        var tileOptions = <olx.layer.TileOptions>{
            id: l.id,
            title: l.text,
            visible: "false" !== options["visible"],
            source: source,
            componentType: l.componentType,
            assetType: l.assetType
        };
        // seems wrong...if extent must be in 4326 why bother providing 4326 as wkid?  Why would it be anything else
        if (l.extent) {
            tileOptions.extent = geo.transformExtent({ wkid: view.options.wkid }, geo.asExtent(l.extent)).geometry;
            log(`${l.id} bound by extent ${JSON.stringify(tileOptions.extent)}`);
        }

        Utils.ifOption(options, "resolutions", minmax => {
            var resolutions = minmax.split(" ").map(parseFloat);
            tileOptions.minResolution = resolutions[0];
            tileOptions.maxResolution = resolutions[1];
        });
        if(view.model.data.Layers.ActiveLayers == undefined)
            view.model.data.Layers.ActiveLayers = [];
        view.model.data.Layers.ActiveLayers.push(tileOptions);
        var tile = new ol.layer.Tile(tileOptions);

        source.on("tileloadstart", () => {
            view.mediator.trigger("layerloadstart", {
                layer: l,
                tile: tile
            });
        });

        source.on("tileloadend", () => {
            view.mediator.trigger("layerloadend", {
                layer: l,
                tile: tile
            });
        });

        // this functionality need to be injected into the map at the tile level
        source.on("refresh", () => {
            var params = source.getParams();
            params["refresh-count"] = (params["refresh-count"] || 0) + 1;
            source.updateParams(params);
            source.changed();
        });

        if (options["refresh-interval"]) {
            let interval = parseFloat(options["refresh-interval"]);
            if (typeof interval === "number") {
                setInterval(() => source.dispatchEvent("refresh"), interval * 1000 * 60);
            }
        }

        EventHandler.register({
            config: l,
            view: view,
            source: source,
            layer: tile
        });

        AgsLayers.setMinMaxZoomLevel(view, tile, l);

        return tile;

    }

    static create(args: { layer: Portal.MapLayerLeaf; view: MapView }): ol.layer.Layer {
        let l = args.layer;
        {
            // to be done server-side and generically across all attributes and options
            l.url = Utils.translate(l.url);
            // Utils.translate should be happening on the server side in the future
            // just being selective/restrictive/expedient for now
            l.Options.Values.forEach(v => v.value = Utils.translate(v.value));
        }
        let options = Utils.asDictionary(l.Options.Values);
        let layerType = options["layerType"] || Handler.options.layerType.default;
        let factory = Handler.tile;
        factory = Handler[layerType];
        return factory && factory(l, args.view);
    }

}

export = Handler; 